// The inline fix for a destination the visitor mistyped: what the editor
// holds, and what Update does with it. Pure, so every state is tested.

import {
  leadFlowEvents,
  type FixContext,
  type LeadFlowEvent,
} from "pages/StartLead/events";
import { isValidEmail, nationalPhoneDigits } from "pages/StartLead/validation";

import type { CorrectionFailure } from "./correction-errors";
import type { CorrectionProof } from "./login-grant-flow";

export type DestinationChannel = "phone" | "email";

/** What the server did with an accepted correction: it needs no
 * confirmation, or it waits on a code for the corrected destination. */
export type CorrectionSaveOutcome = "completed" | "awaiting_confirmation";

/** What a correction came to for the editor: the screen moved on, the
 * visitor can fix the value and try again (`message` says why), or the
 * destination can no longer be changed here. */
export type CorrectionResult =
  { kind: "done"; outcome: CorrectionSaveOutcome } | CorrectionFailure;

/** Saves a corrected destination for the channel the line names. `zip` is the
 * ZIP code from the visitor's form when that is the proof; null otherwise. */
export type DestinationCorrector = (
  channel: DestinationChannel,
  value: string,
  zip: string | null,
) => Promise<CorrectionResult>;

export type DestinationEdit =
  /** `closed` is a destination that can no longer be changed here, which the
   * fix row says in place of "Not right? Fix it". */
  | { kind: "viewing"; closed: boolean }
  | {
      kind: "editing";
      draft: string;
      zip: string;
      error: string | null;
      saving: boolean;
    };

export type DestinationEditEvent =
  | { type: "open"; draft: string }
  | { type: "change"; draft: string }
  | { type: "change_zip"; zip: string }
  | { type: "cancel" }
  | { type: "save_started" }
  | { type: "save_failed"; message: string }
  | { type: "saved" }
  | { type: "save_closed" };

export const VIEWING: DestinationEdit = { kind: "viewing", closed: false };

const CLOSED: DestinationEdit = { kind: "viewing", closed: true };

/** Moves the editor on. An event that does not fit the state (typing while
 * a save is under way, cancelling one) changes nothing. `save_failed` keeps
 * the editor open with what the visitor typed; `saved` and `save_closed` end
 * it, the second leaving the fix row's refusal in place of the fix. */
export function destinationEditReducer(
  state: DestinationEdit,
  event: DestinationEditEvent,
): DestinationEdit {
  if (event.type === "open") {
    if (state.kind !== "viewing" || state.closed) return state;
    return {
      kind: "editing",
      draft: event.draft,
      zip: "",
      error: null,
      saving: false,
    };
  }
  if (state.kind !== "editing") return state;
  switch (event.type) {
    case "change":
      if (state.saving) return state;
      return { ...state, draft: event.draft, error: null };
    case "change_zip":
      if (state.saving) return state;
      return { ...state, zip: event.zip, error: null };
    case "cancel":
      return state.saving ? state : VIEWING;
    case "save_started":
      return { ...state, saving: true, error: null };
    case "save_failed":
      return { ...state, saving: false, error: event.message };
    case "saved":
      return VIEWING;
    case "save_closed":
      return CLOSED;
  }
}

/** The editor's starting text for the destination the line shows: a phone as
 * its ten digits in the lead form's mask, anything else as shown. The
 * visitor with only the ZIP code to go on sees the value masked, so starts
 * from nothing. */
export function prefillFor(
  channel: DestinationChannel,
  display: string,
  proof: NonNullable<CorrectionProof>,
): string {
  if (proof === "zip") return "";
  if (channel === "email") return display;
  const digits = nationalPhoneDigits(display);
  if (digits === null) return display;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function sameDestination(
  channel: DestinationChannel,
  a: string,
  b: string,
): boolean {
  if (channel === "phone") {
    return nationalPhoneDigits(a) === nationalPhoneDigits(b);
  }
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export const PHONE_INCOMPLETE_MESSAGE =
  "Enter all 10 digits of the phone number";
export const EMAIL_INCOMPLETE_MESSAGE = "Enter a full email address";
export const ZIP_INCOMPLETE_MESSAGE = "Enter the 5-digit ZIP code";

const ZIP_PATTERN = /^\d{5}$/;

export type SaveDecision =
  | { kind: "invalid"; message: string }
  /** Update with the value already shown closes the editor; nothing is sent. */
  | { kind: "unchanged" }
  | { kind: "save"; value: string; zip: string | null };

/** What Update does with the draft. The ZIP code is asked for, and required,
 * only where it is the proof. */
export function saveDecision(
  channel: DestinationChannel,
  shown: string,
  { draft, zip }: { draft: string; zip: string },
  proof: NonNullable<CorrectionProof>,
): SaveDecision {
  if (channel === "phone" && nationalPhoneDigits(draft) === null) {
    return { kind: "invalid", message: PHONE_INCOMPLETE_MESSAGE };
  }
  if (channel === "email" && !isValidEmail(draft.trim())) {
    return { kind: "invalid", message: EMAIL_INCOMPLETE_MESSAGE };
  }
  if (proof === "zip") {
    if (!ZIP_PATTERN.test(zip)) {
      return { kind: "invalid", message: ZIP_INCOMPLETE_MESSAGE };
    }
    return { kind: "save", value: draft.trim(), zip };
  }
  if (sameDestination(channel, shown, draft)) return { kind: "unchanged" };
  return { kind: "save", value: draft.trim(), zip: null };
}

/** The event for what Update came to once the draft was checked: the
 * server's answer, or the draft's own failure to pass. */
export function saveEventOf(
  fix: FixContext,
  result: CorrectionResult | { kind: "invalid" },
): LeadFlowEvent {
  if (result.kind === "invalid") {
    return leadFlowEvents.fixRefused(fix, "invalid_input");
  }
  if (result.kind === "done") {
    return leadFlowEvents.fixSaved(fix, result.outcome);
  }
  return leadFlowEvents.fixRefused(fix, result.reason);
}
